import { useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { queryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../auth/AuthProvider';
import { getMachineActivity } from '../api/activity.api';
import { activityFilters, type ActivityCategory, type MachineActivityItemDto } from '../types/activity';
import { IncrementalListFooter } from '../../../components/ui/IncrementalListFooter';
import { ACTIVITY_BATCH_SIZE } from '../../../components/ui/list-batch-sizes';

function iconFor(item: MachineActivityItemDto): string {
  if (item.event_type.startsWith('IMAGE_EVIDENCE_')) return 'bi-file-earmark-image';
  if (item.category === 'DOCUMENTS') return 'bi-file-earmark-text';
  if (item.category === 'INTELLIGENCE') return 'bi-cpu';
  if (item.category === 'CONVERSATIONS') return 'bi-chat-left-text';
  if (item.category === 'INVESTIGATIONS') return 'bi-search';
  return 'bi-plus-circle';
}

function resourceHref(machineId: string, item: MachineActivityItemDto): string | null {
  const resource = item.resource;
  if (!resource?.id) return null;
  if (resource.type === 'DOCUMENT') return `/machines/${machineId}/documents/${resource.id}`;
  if (resource.type === 'MACHINE_MEMORY') return `/machines/${machineId}/memory`;
  if (resource.type === 'CONVERSATION') return `/machines/${machineId}/conversations/${resource.id}`;
  if (resource.type === 'INVESTIGATION') return `/machines/${machineId}/investigations/${resource.id}`;
  if (resource.type === 'MACHINE') return `/machines/${machineId}`;
  return null;
}

function dateKey(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function timeLabel(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function ActivityPage() {
  const { machineId = '' } = useParams();
  const auth = useAuth();
  const [category, setCategory] = useState<ActivityCategory>('ALL');
  const activity = useInfiniteQuery({
    queryKey: queryKeys.activity(auth.activeCustomerId, machineId, category),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => getMachineActivity(machineId, category, pageParam, ACTIVITY_BATCH_SIZE),
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    enabled: Boolean(auth.activeCustomerId && machineId),
  });
  const items = useMemo(() => {
    const seen = new Set<string>();
    return (activity.data?.pages ?? []).flatMap((page) => page.activity).filter((item) => {
      if (seen.has(item.activity_id)) return false;
      seen.add(item.activity_id);
      return true;
    });
  }, [activity.data]);

  const groups = useMemo(() => {
    const map = new Map<string, MachineActivityItemDto[]>();
    for (const item of items) {
      const key = dateKey(item.occurred_at);
      map.set(key, [...(map.get(key) ?? []), item]);
    }
    return [...map.entries()];
  }, [items]);

  return (
    <main className="r42-activity-page">
      <header className="r42-activity-header">
        <div>
          <span className="r42-eyebrow">Machine history</span>
          <h1>Activity</h1>
          <p>Meaningful changes and work for this machine. Compliance audit events remain separate.</p>
        </div>
      </header>

      <div className="r42-activity-filters" aria-label="Activity filters">
        {activityFilters.map((filter) => (
          <button
            key={filter.value}
            type="button"
            className={category === filter.value ? 'is-active' : ''}
            aria-pressed={category === filter.value}
            onClick={() => setCategory(filter.value)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {activity.isPending ? (
        <section className="r42-activity-loading" data-testid="activity-loading" aria-label="Loading machine activity">
          <div /><div /><div />
        </section>
      ) : activity.isError && !activity.data ? (
        <section className="r42-state-panel">
          <span className="r42-eyebrow">Activity unavailable</span>
          <h2>Route42 couldn't load this machine's activity.</h2>
          <p>Try again. Your machine data has not been changed.</p>
          <button type="button" className="btn btn-outline-secondary" onClick={() => activity.refetch()}>Try again</button>
        </section>
      ) : groups.length === 0 ? (
        <section className="r42-state-panel">
          <span className="r42-eyebrow">No matching activity</span>
          <h2>Nothing to show for this filter yet.</h2>
          <p>Route42 will surface meaningful machine work here as it happens.</p>
        </section>
      ) : (
        <div className="r42-activity-timeline">
          {groups.map(([date, items]) => (
            <section key={date} className="r42-activity-day">
              <h2>{date}</h2>
              <div className="r42-activity-list">
                {items.map((item) => {
                  const href = resourceHref(machineId, item);
                  const content = (
                    <>
                      <span className="r42-activity-icon" aria-hidden="true"><i className={`bi ${iconFor(item)}`} /></span>
                      <span className="r42-activity-copy">
                        <span className="r42-activity-row-head">
                          <strong>{item.title}</strong>
                          <time dateTime={item.occurred_at}>{timeLabel(item.occurred_at)}</time>
                        </span>
                        <span>{item.summary}</span>
                      </span>
                      {href ? <i className="bi bi-chevron-right r42-activity-chevron" aria-hidden="true" /> : null}
                    </>
                  );
                  return href ? (
                    <Link key={item.activity_id} className="r42-activity-row" to={href}>{content}</Link>
                  ) : (
                    <div key={item.activity_id} className="r42-activity-row">{content}</div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
      <IncrementalListFooter label="activity items" loaded={items.length} hasMore={Boolean(activity.hasNextPage)} loading={activity.isFetchingNextPage} error={activity.isFetchNextPageError} onLoadMore={() => void activity.fetchNextPage()} />
    </main>
  );
}
